import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { getDashboard, getLicitacoesAlertas } from '@/services/api'
import { formatCurrency } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  FileText,
  Gavel,
  Stethoscope,
  Fuel,
  ClipboardList,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Activity,
  ArrowRight,
  Clock
} from 'lucide-react'
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts'
import { toast } from 'sonner'

export default function DashboardPage() {
  const { hasPermission } = useAuth()
  const [data, setData] = useState(null)
  const [alertas, setAlertas] = useState([])
  const [loading, setLoading] = useState(true)

  const canSee = (mod) => hasPermission(mod)
  const showProcessos = canSee('processos')
  const showLicitacoes = canSee('licitacoes')
  const showEspecialistas = canSee('especialistas')
  const showAbastecimento = canSee('abastecimento')
  const showAtendimentos = canSee('atendimentos')
  const showIndicadores = canSee('indicadores')

  const fetchData = useCallback(async () => {
    try {
      const requests = [getDashboard()]
      if (showLicitacoes) requests.push(getLicitacoesAlertas())
      const responses = await Promise.all(requests)
      setData(responses[0].data)
      if (showLicitacoes && responses[1]) setAlertas(responses[1].data)
    } catch (error) {
      toast.error('Erro ao carregar dashboard')
    } finally {
      setLoading(false)
    }
  }, [showLicitacoes])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    )
  }

  const processosChartData = data ? [
    { name: 'Abertos', value: data.processos.abertos, color: '#64748B' },
    { name: 'Pronto Pgto', value: data.processos.prontos_pagamento, color: '#F59E0B' },
    { name: 'Pagos', value: data.processos.pagos, color: '#10B981' },
  ] : []

  const statCards = [
    showProcessos && (
      <Card key="processos" data-testid="stat-processos">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Processos</p>
              <p className="text-2xl font-semibold text-slate-900">{data?.processos.total || 0}</p>
              <p className="text-sm text-slate-500 mt-1">{formatCurrency(data?.processos.valor_abertos || 0)} em aberto</p>
            </div>
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-teal-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    ),
    showLicitacoes && (
      <Card key="licitacoes" data-testid="stat-licitacoes">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Licitações</p>
              <p className="text-2xl font-semibold text-slate-900">{data?.licitacoes.total || 0}</p>
              <p className="text-sm text-amber-600 mt-1">{data?.licitacoes.alertas || 0} com alerta</p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Gavel className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    ),
    showEspecialistas && (
      <Card key="especialistas" data-testid="stat-especialistas">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Especialistas</p>
              <p className="text-2xl font-semibold text-slate-900">{data?.especialistas.total || 0}</p>
              <p className="text-sm text-emerald-600 mt-1">{data?.especialistas.consultas_realizadas || 0} consultas</p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    ),
    showAbastecimento && (
      <Card key="abastecimento" data-testid="stat-abastecimento">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Abastecimento</p>
              <p className="text-2xl font-semibold text-slate-900">{data?.abastecimentos.total || 0}</p>
              <p className="text-sm text-slate-500 mt-1">{formatCurrency(data?.abastecimentos.valor_total || 0)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Fuel className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    ),
    showAtendimentos && data?.atendimentos && (
      <Card key="atendimentos" data-testid="stat-atendimentos">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Atendimentos</p>
              <p className="text-2xl font-semibold text-slate-900">{data.atendimentos.total || 0}</p>
              <p className="text-sm text-slate-500 mt-1">Total registrados</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    ),
  ].filter(Boolean)

  const quickActions = [
    showProcessos && { to: '/processos', icon: FileText, label: 'Processos', hover: 'hover:bg-teal-50 hover:border-teal-200', color: 'text-teal-600', testid: 'quick-processos' },
    showLicitacoes && { to: '/licitacoes', icon: Gavel, label: 'Licitações', hover: 'hover:bg-amber-50 hover:border-amber-200', color: 'text-amber-600', testid: 'quick-licitacoes' },
    showEspecialistas && { to: '/especialistas', icon: Stethoscope, label: 'Especialistas', hover: 'hover:bg-emerald-50 hover:border-emerald-200', color: 'text-emerald-600', testid: 'quick-especialistas' },
    showAbastecimento && { to: '/abastecimento', icon: Fuel, label: 'Abastecimento', hover: 'hover:bg-blue-50 hover:border-blue-200', color: 'text-blue-600', testid: 'quick-abastecimento' },
    showAtendimentos && { to: '/atendimentos', icon: ClipboardList, label: 'Atendimentos', hover: 'hover:bg-purple-50 hover:border-purple-200', color: 'text-purple-600', testid: 'quick-atendimentos' },
    showIndicadores && { to: '/indicadores', icon: TrendingUp, label: 'Indicadores', hover: 'hover:bg-rose-50 hover:border-rose-200', color: 'text-rose-600', testid: 'quick-indicadores' },
  ].filter(Boolean)

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Dashboard</h1>
        <p className="text-slate-500 mt-1">Visão geral do Sistema Saúde Pão de Açúcar</p>
      </div>

      {/* Alertas de Licitação — só admins/usuários com permissão */}
      {showLicitacoes && alertas.length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="alertas-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              Licitações Próximas do Vencimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alertas.slice(0, 3).map((alerta) => (
                <div key={alerta.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div>
                    <p className="font-medium text-slate-900">{alerta.numero_processo}</p>
                    <p className="text-sm text-slate-500">{alerta.objeto}</p>
                  </div>
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                    <Clock className="w-3 h-3 mr-1" />
                    {alerta.dias_para_vencer} dias
                  </Badge>
                </div>
              ))}
            </div>
            <Link to="/licitacoes">
              <Button variant="ghost" className="mt-3 text-amber-700 hover:text-amber-800 hover:bg-amber-100 w-full">
                Ver todas as licitações
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {statCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards}
        </div>
      )}

      {/* Charts Row — só aparece se o usuário tem acesso a Processos */}
      {showProcessos && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="chart-processos">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-teal-600" />
                Status dos Processos
              </CardTitle>
              <CardDescription>Distribuição por status de pagamento</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={processosChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                      {processosChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, 'Quantidade']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-4 flex-wrap">
                {processosChartData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-slate-600">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-valores">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-teal-600" />
                Resumo Financeiro
              </CardTitle>
              <CardDescription>Valores dos processos por status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Processos em Aberto</span>
                    <span className="text-xl font-semibold text-slate-900">{formatCurrency(data?.processos.valor_abertos || 0)}</span>
                  </div>
                  <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-500 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-700">Pronto para Pagamento</span>
                    <span className="text-xl font-semibold text-amber-800">{formatCurrency(data?.processos.valor_prontos || 0)}</span>
                  </div>
                  <div className="mt-2 h-2 bg-amber-200 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-emerald-700">Processos Pagos</span>
                    <span className="text-xl font-semibold text-emerald-800">{formatCurrency(data?.processos.valor_pagos || 0)}</span>
                  </div>
                  <div className="mt-2 h-2 bg-emerald-200 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
            <CardDescription>Acesse os módulos do sistema</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {quickActions.map(({ to, icon: Icon, label, hover, color, testid }) => (
                <Link key={to} to={to}>
                  <Button variant="outline" className={`w-full h-auto py-4 flex flex-col gap-2 ${hover}`} data-testid={testid}>
                    <Icon className={`w-6 h-6 ${color}`} />
                    <span>{label}</span>
                  </Button>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mensagem caso não tenha acesso a nenhum módulo */}
      {statCards.length === 0 && quickActions.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <p className="mb-2">Você ainda não tem acesso a nenhum módulo.</p>
            <p className="text-sm">Solicite ao administrador para configurar suas permissões.</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
